<template>
  <v-dialog :value="isModalActive" max-width="480" @input="inputModal">
    <v-card
      id="tag-manager-modal-card"
      class="pb-2"
      :color="$vuetify.theme.dark ? 'dark_backgroundDarkBlue' : undefined"
    >
      <v-card-text class="py-0 pt-5 pb-3">
        <div class="items-flex justify-between">
          <div class="card-title">
            {{ $t('views.common.tagManagerModal.title') }}
          </div>
          <v-icon size="20" @click="closeModal">mdi-close</v-icon>
        </div>

        <v-text-field
          v-model="search"
          class="mt-4"
          :placeholder="$t('views.common.tagManagerModal.search')"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          hide-details
          clearable
        />

        <div class="tag-manager-list mt-4">
          <div v-if="loading" class="text-center py-6">
            <v-progress-circular indeterminate color="primary" size="28" />
          </div>

          <div v-else-if="!filteredTags.length" class="text-center py-6 empty-text">
            {{ search ? $t('views.common.tagManagerModal.noResults') : $t('views.common.tagManagerModal.empty') }}
          </div>

          <template v-else>
            <div v-for="item in filteredTags" :key="item.tag" class="tag-manager-row my-2">
              <template v-if="editingTag === item.tag">
                <v-text-field
                  v-model="editValue"
                  class="tag-manager-edit-input"
                  dense
                  outlined
                  hide-details
                  autofocus
                  maxlength="30"
                  :disabled="savingTag === item.tag"
                  @keyup.enter="confirmRename(item.tag)"
                  @keyup.esc="cancelRename"
                />
                <v-icon
                  class="ml-2"
                  color="primary"
                  size="22"
                  :disabled="savingTag === item.tag"
                  @click="confirmRename(item.tag)"
                >
                  mdi-check
                </v-icon>
                <v-icon
                  class="ml-1"
                  size="22"
                  :disabled="savingTag === item.tag"
                  @click="cancelRename"
                >
                  mdi-close
                </v-icon>
              </template>

              <template v-else>
                <div class="tag-manager-bar" :style="barStyle">
                  <span class="tag-manager-name">{{ item.tag }}</span>
                  <span class="tag-manager-count">{{ item.count }}</span>
                </div>
                <v-icon class="ml-2" size="19" :disabled="!!savingTag" @click="startRename(item.tag)">
                  mdi-pencil-outline
                </v-icon>
                <v-icon class="ml-1" size="19" :disabled="!!savingTag" @click="removeTag(item.tag)">
                  mdi-trash-can-outline
                </v-icon>
              </template>
            </div>
          </template>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { getPositionTags, renamePositionTag, deletePositionTag } from '@/lib/api.js'
import modalMixin from '@/views/components/core/modalMixin.js'

export default {
  mixins: [modalMixin],
  data: () => ({
    loading: false,
    tags: [],
    search: '',
    editingTag: null,
    editValue: '',
    savingTag: null,
  }),
  computed: {
    barStyle() {
      if (!this.$vuetify.theme.dark) return {}
      const theme = this.$vuetify.theme.currentTheme
      // slightly lighter than the modal background (dark_backgroundDarkBlue)
      return {
        backgroundColor: theme.dark_backgroundSoftBlue,
        borderColor: theme.dark_backgroundGrey,
      }
    },
    filteredTags() {
      const search = (this.search || '').trim().toLowerCase()
      const tags = [...this.tags].sort((a, b) => a.tag.localeCompare(b.tag))
      if (!search) return tags
      return tags.filter((item) => item.tag.toLowerCase().includes(search))
    },
  },
  watch: {
    isModalActive: {
      handler(active) {
        if (active) this.loadTags()
      },
      immediate: true,
    },
  },
  methods: {
    resetForm() {
      this.search = ''
      this.editingTag = null
      this.editValue = ''
      this.savingTag = null
    },
    async loadTags() {
      this.loading = true
      try {
        const { data } = await getPositionTags()
        this.tags = data || []
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loading = false
    },
    startRename(tag) {
      this.editingTag = tag
      this.editValue = tag
    },
    cancelRename() {
      this.editingTag = null
      this.editValue = ''
    },
    async confirmRename(oldName) {
      const newName = (this.editValue || '').trim()
      if (!newName || newName === oldName) {
        this.cancelRename()
        return
      }

      this.savingTag = oldName
      try {
        await renamePositionTag({ oldName, newName })
        this.$root.$snackbar.success({ text: this.$t('common.saveOk') })
        this.cancelRename()
        await this.loadTags()
        this.$emit('saved')
      } catch (error) {
        this.$root.$snackbar.error({ text: this.$t('common.saveError') })
      }
      this.savingTag = null
    },
    async removeTag(tag) {
      const confirmed = await this.$root.$confirm({
        title: this.$t('views.common.tagManagerModal.deleteTitle'),
        message: this.$t('views.common.tagManagerModal.deleteMessage', { tag }),
      })
      if (!confirmed) return

      this.savingTag = tag
      try {
        await deletePositionTag({ name: tag })
        this.$root.$snackbar.success({ text: this.$t('common.saveOk') })
        await this.loadTags()
        this.$emit('saved')
      } catch (error) {
        this.$root.$snackbar.error({ text: this.$t('common.saveError') })
      }
      this.savingTag = null
    },
  },
}
</script>

<style lang="scss">
#tag-manager-modal-card {
  .items-flex {
    display: flex;
    align-items: center;
    &.justify-between {
      justify-content: space-between;
    }
  }

  .card-title {
    font-size: 18px;
    font-weight: 500;
  }

  .tag-manager-list {
    max-height: 55vh;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .empty-text {
    font-size: 13px;
    opacity: 0.7;
  }

  .tag-manager-row {
    display: flex;
    align-items: center;
  }

  .tag-manager-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1 1 auto;
    min-width: 0;
    border: 1px solid #eeeeee;
    border-radius: 5px;
    background-color: #f7f8fb;
    padding: 8px 12px;
    font-size: 13px;
    font-weight: 400;
    line-height: 1.2;

    .tag-manager-name {
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .tag-manager-count {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-left: 10px;
      min-width: 22px;
      height: 22px;
      padding: 0 6px;
      border-radius: 11px;
      background-color: rgba(0, 0, 0, 0.08);
      color: rgba(0, 0, 0, 0.6);
      font-size: 12px;
      font-weight: 500;
      line-height: 1;
    }
  }

  .tag-manager-edit-input {
    flex: 1 1 auto;
    min-width: 0;
  }
}

#tag-manager-modal-card.theme--dark {
  .tag-manager-bar .tag-manager-count {
    background-color: rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.75);
  }
}
</style>
